import React from 'react'
import {
  Activity,
  BarChart3,
  Building2,
  Car,
  Database,
  ExternalLink,
  Flame,
  Hospital,
  Landmark,
  Pill,
  PiggyBank,
  RefreshCw,
  ShieldCheck,
  Stethoscope,
  Trash2,
  UsersRound,
} from 'lucide-react'
import { api, getApiErrorMessage } from '../lib/api'
import { formatApiDateTimeDisplay } from '../lib/date'

const DATASETS = [
  {
    id: 'fire',
    title: '소방청 화재발생 정보',
    sourceName: 'NFA_FIRE_OCCURRENCE',
    icon: Flame,
    cadence: '월 1회',
    statusKind: 'fire',
    syncKind: 'fire',
    syncLabel: '수동 가져오기',
    uploadKind: 'fireCsv',
    uploadLabel: 'CSV 업로드',
    uploadAccept: '.csv,text/csv',
    collectionMode: '저장형 동기화',
    queryMode: '업무 화면은 저장된 통계 조회',
    externalCallMode: '월 1회 배치 또는 수동 가져오기 때 원천 URL 호출',
    storageMode: '버전별 원천 이력과 지역·원인·장소 통계 저장',
    manualMode: '수동 가져오기, CSV 업로드',
    useCase: '화재보험설계 STEP4 특약 추천',
    sourceLinks: [
      { label: '소방청 화재발생 정보', url: 'https://www.data.go.kr/data/15044003/fileData.do' },
    ],
    primaryPlacement: '화재보험설계',
    screenPlan: '기존 화재보험설계 STEP4의 가입 유형/특약 영역에 지역 화재 통계를 함께 보여줍니다.',
    dataPlan: '월별·지역별·장소별·원인별 통계를 저장하고, 주소의 시도/시군구 기준으로 요약 조회합니다.',
    decision: '기존 화면 확장',
  },
  {
    id: 'hospital',
    title: '심평원 병원정보/의료기관 상세',
    icon: Hospital,
    cadence: '일 1회 또는 주 1회',
    statusKind: 'hiraInstitution',
    syncKind: 'hiraInstitutionReload',
    syncLabel: '캐시 다시 읽기',
    uploadKind: 'hiraInstitutionFile',
    uploadLabel: '기관 파일 업로드',
    uploadAccept: '.csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel',
    collectionMode: 'DB 우선 + 실시간 보강',
    queryMode: '고객지원도구 검색 시 기관 마스터 조회, 없으면 병원정보 API 조회',
    externalCallMode: '검색 fallback 또는 파일 업로드/리로드 때 반영',
    storageMode: '의료기관 마스터 DB와 인메모리 검색 인덱스',
    manualMode: '기관 파일 업로드, 캐시 다시 읽기',
    useCase: '병원 검색, 청구 안내, 실손 상담',
    sourceLinks: [
      { label: '병원정보서비스', url: 'https://www.data.go.kr/data/15001698/openapi.do' },
      { label: '의료기관별상세정보서비스', url: 'https://www.data.go.kr/data/15001699/openapi.do' },
    ],
    primaryPlacement: '고객지원도구',
    screenPlan: '고객지원도구의 병원정보 탭으로 두고, 병원명·지역 기준 검색 결과를 상담 중 확인하도록 구성합니다.',
    dataPlan: '기관 기본정보는 캐시 테이블로 관리하고, 상세 조회는 필요 항목만 보강합니다.',
    decision: '기존 화면 확장',
  },
  {
    id: 'nonPayment',
    title: '심평원 비급여진료비 정보',
    icon: Stethoscope,
    cadence: '주 1회',
    statusKind: 'openDataCache',
    cacheProvider: 'HIRA_NON_PAYMENT',
    collectionMode: 'TTL 캐시 우선 조회',
    queryMode: '고객지원도구 검색 시 24시간 캐시 조회 후 미스/만료 때 비급여 API 호출',
    externalCallMode: '캐시 미스 또는 만료된 검색 조건에서만 공공 API 호출',
    storageMode: '검색 조건별 API 응답을 24시간 TTL로 저장',
    manualMode: '상태 새로고침, 캐시 비우기',
    useCase: '비급여 항목 가격 비교, 병원비 상담',
    sourceLinks: [
      { label: '비급여진료비정보조회서비스', url: 'https://www.data.go.kr/data/15001700/openapi.do' },
    ],
    primaryPlacement: '고객지원도구',
    screenPlan: '고객지원도구의 비급여진료비 탭으로 두고, 병원명 또는 비급여 항목코드 기준으로 조회합니다.',
    dataPlan: '검색 조건별 응답 캐시를 먼저 사용하고, 항목 코드와 병원별 금액은 추후 분석/비교 화면이 확정되면 별도 정규화 저장을 검토합니다.',
    decision: '기존 화면 확장',
  },
  {
    id: 'drug',
    title: '식약처 e약은요',
    icon: Pill,
    cadence: '주 1회',
    statusKind: 'openDataCache',
    cacheProvider: 'MFDS_EASY_DRUG',
    collectionMode: 'TTL 캐시 우선 조회',
    queryMode: '고객지원도구 검색 시 24시간 캐시 조회 후 미스/만료 때 e약은요 API 호출',
    externalCallMode: '캐시 미스 또는 만료된 검색 조건에서만 공공 API 호출',
    storageMode: '검색 조건별 API 응답을 24시간 TTL로 저장',
    manualMode: '상태 새로고침, 캐시 비우기',
    useCase: '설계사 의약품 검색',
    sourceLinks: [
      { label: '의약품개요정보(e약은요)', url: 'https://www.data.go.kr/data/15075057/openapi.do' },
    ],
    primaryPlacement: '고객지원도구',
    screenPlan: '고객에게 직접 노출하기보다는 고객지원도구의 e약은요 검색 탭으로 두고 설계사 확인용으로 사용합니다.',
    dataPlan: '의약품명·업체명·품목기준코드 중심의 검색 응답을 캐시하고, 상세 복약 문구는 상담 근거 확인용으로만 다룹니다.',
    decision: '기존 화면 확장',
  },
  {
    id: 'medicalFeeDrugPrice',
    title: '진료수가/약가 기준 정보',
    icon: Database,
    cadence: '주 1회',
    statusKind: 'openDataCache',
    cacheProvider: 'HIRA_MDFEE',
    collectionMode: 'TTL 캐시 우선 조회',
    queryMode: '기준정보 검색 시 24시간 캐시 조회 후 미스/만료 때 수가기준 API 호출',
    externalCallMode: '캐시 미스 또는 만료된 검색 조건에서만 공공 API 호출',
    storageMode: '수가 기준 API 응답을 24시간 TTL로 저장',
    manualMode: '상태 새로고침, 캐시 비우기',
    useCase: '수가·약가 기준 확인',
    sourceLinks: [
      { label: '수가기준정보조회서비스', url: 'https://www.data.go.kr/data/15021028/openapi.do' },
      { label: '약가기준정보조회서비스', url: 'https://www.data.go.kr/data/15054445/openapi.do' },
    ],
    primaryPlacement: '보류',
    screenPlan: '활용 화면은 조금 더 검토합니다. 고객지원도구에 바로 노출하기보다 설계/보상 기준정보와의 연결 지점을 먼저 정리합니다.',
    dataPlan: '수가 기준 검색 응답은 캐시해두고, 약가 기준 연결과 화면 노출 범위는 별도로 결정합니다.',
    decision: '검토 보류',
  },
  {
    id: 'kcd',
    title: 'KCD/질병 통계류',
    icon: Activity,
    cadence: '주 1회 또는 자료 배포 시',
    statusKind: 'hiraDiseaseStats',
    uploadKind: 'hiraDiseaseStatsXlsx',
    uploadLabel: '질병 통계 XLSX 업로드',
    uploadAccept: '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    collectionMode: '파일/릴리스 관리 + 일부 API 조회',
    queryMode: '서비스 화면은 활성 릴리스와 KCD 마스터 조회',
    externalCallMode: '질병정보 API는 검색형, 통계류는 자료 배포 시 파일 반영',
    storageMode: 'KCD 마스터와 HIRA 질병통계 릴리스 저장',
    manualMode: '질병 통계 XLSX 업로드, 상태 새로고침',
    useCase: '질병 코드, 진단비/수술비 상담 근거',
    sourceLinks: [
      { label: '질병정보서비스', url: 'https://www.data.go.kr/data/15119055/openapi.do' },
      { label: '3단상병 통계', url: 'https://www.data.go.kr/data/15138832/fileData.do' },
      { label: '4단상병 통계', url: 'https://www.data.go.kr/data/15138833/fileData.do' },
    ],
    primaryPlacement: '설계지원 DB',
    screenPlan: '현재 관리 중인 KCD 질병코드, 질병코드·특약, N대 질병, 질병 진료비 통계와 겹치는 범위를 먼저 비교합니다.',
    dataPlan: 'KCD 마스터와 외부 통계 릴리스를 분리해 버전 관리하고, 기존 데이터와 코드·질병명·통계 기준의 중복률을 검토합니다.',
    decision: '겹침 검토 선행',
  },
  {
    id: 'fscLifeCompany',
    title: '금융위 생보사 일반현황',
    sourceName: 'FSC_LIFE_INSURANCE_COMPANY_GENERAL',
    syncKind: 'life',
    syncLabel: '수동 가져오기',
    icon: Building2,
    cadence: '주 1회',
    statusKind: 'fscCompany',
    collectionMode: '저장형 동기화',
    queryMode: '운영자/리포트 화면은 저장된 metric 조회',
    externalCallMode: '수동 가져오기 또는 주 1회 배치 때 공공 API 호출',
    storageMode: '보험사·기준년월별 metric과 raw JSON 저장',
    manualMode: '수동 가져오기',
    useCase: '보험사/시장 현황 리포트',
    sourceLinks: [
      { label: '금융통계생명보험정보', url: 'https://www.data.go.kr/data/15061306/openapi.do' },
    ],
    primaryPlacement: '운영자/리더 리포트',
    screenPlan: '고객 상담 화면보다 운영자·리더용 시장/보험사 통계 화면으로 시작하는 것을 추천합니다.',
    dataPlan: '기준년월, 보험사명/코드, 원천 항목명과 수치형 지표를 metric 형태로 저장합니다. 지급여력, 자산, 부채, 자본, 손익, 계약/보험료류 항목을 원천 응답 그대로 확장 수용합니다.',
    decision: '데이터 확인 후 리포트',
  },
  {
    id: 'fscNonlifeCompany',
    title: '금융위 손보사 일반현황',
    sourceName: 'FSC_NONLIFE_INSURANCE_COMPANY_GENERAL',
    syncKind: 'nonlife',
    syncLabel: '수동 가져오기',
    icon: Building2,
    cadence: '주 1회',
    statusKind: 'fscCompany',
    collectionMode: '저장형 동기화',
    queryMode: '운영자/리포트 화면은 저장된 metric 조회',
    externalCallMode: '수동 가져오기 또는 주 1회 배치 때 공공 API 호출',
    storageMode: '보험사·기준년월별 metric과 raw JSON 저장',
    manualMode: '수동 가져오기',
    useCase: '보험사/시장 현황 리포트',
    sourceLinks: [
      { label: '금융통계손해보험정보', url: 'https://www.data.go.kr/data/15061307/openapi.do' },
    ],
    primaryPlacement: '운영자/리더 리포트',
    screenPlan: '고객 상담 화면보다 운영자·리더용 시장/보험사 통계 화면으로 시작하는 것을 추천합니다.',
    dataPlan: '기준년월, 보험사명/코드, 원천 항목명과 수치형 지표를 metric 형태로 저장합니다. 손보사 자산/부채/자본, 손익, 보험료/계약류 지표를 원천 응답 그대로 확장 수용합니다.',
    decision: '데이터 확인 후 리포트',
  },
  {
    id: 'generalInsurance',
    title: '일반손해보험가입정보',
    sourceName: 'FSC_GENERAL_NONLIFE_SUBSCRIPTION',
    icon: ShieldCheck,
    cadence: '주 1회',
    statusKind: 'fscGeneral',
    syncKind: 'general',
    syncLabel: '수동 가져오기',
    collectionMode: '저장형 동기화',
    queryMode: '추후 일반보험 화면은 저장된 가입 통계 조회',
    externalCallMode: '수동 가져오기 또는 주 1회 배치 때 공공 API 호출',
    storageMode: '연도·담보·물건별 계약건수와 보험료 통계 저장',
    manualMode: '수동 가져오기',
    useCase: '기업성 재물보험 시장 수요, 담보/물건별 추세',
    sourceLinks: [
      { label: '일반손해보험가입정보', url: 'https://www.data.go.kr/data/15124894/openapi.do' },
    ],
    primaryPlacement: 'BE 우선 구현',
    screenPlan: 'FE 화면은 추후 일반보험 메뉴가 열릴 때 연결하고, 지금은 BE 동기화·조회 API와 저장 구조를 먼저 유지합니다.',
    dataPlan: '기업성 재물(재산종합) 보험가입정보를 연도·담보·물건 유형별 계약건수와 보험료 통계로 저장하고, 일반보험 설계 화면의 추천 근거로 연결할 수 있게 둡니다.',
    decision: 'BE 선구현',
  },
]

const SOURCE_LINKS = [
  { label: '공공데이터포털', url: 'https://www.data.go.kr/index.do' },
  { label: '화재보험설계 화면', url: '/fire/building-registry', internal: true },
  { label: '고객지원도구', url: '/support/customer-tools', internal: true },
  { label: '보험시장 인사이트', url: '/work/insurance-market-insights', internal: true },
]

const INLINE_ERROR_REQUEST_CONFIG = { __silent: true, __suppressErrorPageRedirect: true }

const INSURANCE_COMPANY_FIELDS = [
  { label: '기준', text: '기준년월, 보험사명, 보험사 코드' },
  { label: '재무', text: '자산, 부채, 자본, 손익 등 수치형 지표' },
  { label: '영업', text: '계약, 보험료, 지급·손해 관련 원천 지표' },
  { label: '저장 방식', text: '원천 필드명을 metric으로 저장하고 raw JSON을 함께 보관' },
]

const DATA_DETAIL_FIELDS = [
  { key: 'collectionMode', label: '수집 방식' },
  { key: 'queryMode', label: '조회 방식' },
  { key: 'externalCallMode', label: '외부 호출' },
  { key: 'storageMode', label: '저장/캐시' },
  { key: 'manualMode', label: '수동 기능' },
]

const INSIGHT_DATASETS = [
  {
    id: 'life',
    title: '생명보험 가입현황',
    icon: ShieldCheck,
    sourceName: 'FSC_LIFE_INSURANCE_JOIN_STATUS',
    statusKind: 'fscMarket',
    syncKind: 'market:lifeJoinStatus',
    syncLabel: '수동 가져오기',
    status: '자동화 가능',
    cadence: '월 1회',
    source: '금융위원회 생명보험가입정보',
    sourceLinks: [
      { label: '생명보험가입정보', url: 'https://www.data.go.kr/data/15124892/openapi.do' },
    ],
    collectionMode: '공공데이터포털 Open API',
    automationMode: '공통 일반 인증키로 배치 수집 가능',
    requiredAction: '활용신청 완료 후 수동 가져오기 또는 월간 배치로 수집',
    targetScreen: '보험시장 인사이트 > 생명보험',
    metrics: '보험종류별 가입건수, 가입률, 연령/성별/지역별 현황',
  },
  {
    id: 'annuity',
    title: '개인연금 가입현황',
    icon: PiggyBank,
    sourceName: 'FSC_INDIVIDUAL_ANNUITY_INSURANCE',
    statusKind: 'fscMarket',
    syncKind: 'market:individualAnnuity',
    syncLabel: '수동 가져오기',
    status: '자동화 가능',
    cadence: '월 1회',
    source: '금융위원회 생명보험가입정보',
    sourceLinks: [
      { label: '생명보험가입정보', url: 'https://www.data.go.kr/data/15124892/openapi.do' },
    ],
    collectionMode: '공공데이터포털 Open API',
    automationMode: '생명보험 가입현황 수집기와 같은 API에서 함께 수집 가능',
    requiredAction: '수동 가져오기 또는 월간 배치로 수집, 응답이 없으면 오퍼레이션 path 확인',
    targetScreen: '보험시장 인사이트 > 개인연금',
    metrics: '연령별 가입건수, 세제적격, 납입방법, 모집형태',
  },
  {
    id: 'nonlife',
    title: '손해보험 종목별 계약통계',
    icon: Landmark,
    status: '별도 키 필요',
    cadence: '월 1회 또는 분기 1회',
    source: 'KOSIS 손해보험사별 계약 및 손해상황',
    sourceLinks: [
      { label: 'KOSIS 통계표', url: 'https://kosis.kr/statHtml/statHtml.do?orgId=382&tblId=TX_38202_A159' },
      { label: 'KOSIS Open API', url: 'https://kosis.kr/openapi/' },
    ],
    collectionMode: 'KOSIS Open API',
    automationMode: 'KOSIS 인증키 발급 후 자동 수집 가능',
    requiredAction: 'KOSIS 인증키 발급, 통계표/분류 코드 매핑',
    targetScreen: '보험시장 인사이트 > 손해보험',
    metrics: '보험사별 계약건수, 종목별 수입보험료, 손해상황',
  },
  {
    id: 'auto',
    title: '자동차보험 가입정보',
    icon: Car,
    sourceName: 'FSC_AUTO_INSURANCE_CONTRACT',
    statusKind: 'fscMarket',
    syncKind: 'market:autoContract',
    syncLabel: '수동 가져오기',
    status: '자동화 가능',
    cadence: '월 1회',
    source: '금융위원회 자동차보험가입정보',
    sourceLinks: [
      { label: '자동차보험가입정보', url: 'https://www.data.go.kr/data/15124891/openapi.do' },
    ],
    collectionMode: '공공데이터포털 Open API',
    automationMode: '공통 일반 인증키로 배치 수집 가능',
    requiredAction: '계약정보 우선 수집, 손해/피해자 오퍼레이션은 추가 수집 범위로 관리',
    targetScreen: '보험시장 인사이트 > 자동차보험',
    metrics: '차종별 가입대수, 담보별 가입대수, 연령별 계약/손해 통계',
  },
  {
    id: 'general',
    title: '일반손해보험 가입정보',
    icon: Building2,
    sourceName: 'FSC_GENERAL_NONLIFE_SUBSCRIPTION',
    statusKind: 'fscGeneral',
    syncKind: 'general',
    syncLabel: '수동 가져오기',
    status: '일부 구현됨',
    cadence: '월 1회',
    source: '금융위원회 일반손해보험가입정보',
    sourceLinks: [
      { label: '일반손해보험가입정보', url: 'https://www.data.go.kr/data/15124894/openapi.do' },
    ],
    collectionMode: '공공데이터포털 Open API',
    automationMode: '기존 일반손해보험가입정보 수동 가져오기/저장 구조와 연결 가능',
    requiredAction: '수동 가져오기 후 보험시장 인사이트 화면에서 담보·물건 조합 확인',
    targetScreen: '보험시장 인사이트 > 일반손해보험',
    metrics: '담보구분별 계약건수, 물건구분별 보험료 총액',
  },
  {
    id: 'channel',
    title: '판매채널 통계',
    icon: UsersRound,
    status: '반자동 권장',
    cadence: '월 1회',
    source: '생명보험협회 판매통계',
    sourceLinks: [
      { label: '생명보험협회 판매통계', url: 'https://www.klia.or.kr/consumer/stats/statHomSta/monthStats.do?viewGb=sale' },
    ],
    collectionMode: '협회 자료 확인 후 파일 업로드 또는 수집기 구현',
    automationMode: '안정적인 공식 API 확인 전까지 운영자 업로드가 안전',
    requiredAction: '다운로드 파일 포맷 확인, 업로드 파서 설계',
    targetScreen: '보험시장 인사이트 > 판매채널',
    metrics: '설계사, 대리점, 방카, CM, TM 채널별 판매 규모',
  },
  {
    id: 'insurer',
    title: '보험사 비교 지표',
    icon: BarChart3,
    status: '일부 자동화',
    cadence: '월 1회 또는 분기 1회',
    source: '금융위 보험사 일반현황 + KOSIS',
    sourceLinks: [
      { label: '금융통계생명보험정보', url: 'https://www.data.go.kr/data/15061306/openapi.do' },
      { label: '금융통계손해보험정보', url: 'https://www.data.go.kr/data/15061307/openapi.do' },
    ],
    collectionMode: '공공데이터포털 Open API + KOSIS 보강',
    automationMode: '금융위 일반현황은 기존 수집 구조 활용, KOSIS 지표는 별도 연동',
    requiredAction: '보험사 코드 표준화와 비교 지표 선정',
    targetScreen: '보험시장 인사이트 > 보험사 비교',
    metrics: '보험사별 계약건수, 수입보험료, 주요 영업/재무 지표',
  },
]

const INSIGHT_DETAIL_FIELDS = [
  { key: 'collectionMode', label: '수집 방식' },
  { key: 'automationMode', label: '자동화' },
  { key: 'requiredAction', label: '운영 필요' },
  { key: 'targetScreen', label: '연결 화면' },
  { key: 'metrics', label: '주요 지표' },
]

function unwrapApiData(res) {
  const body = res?.data
  if (body?.success === false) {
    throw new Error(body?.message || '요청 처리에 실패했습니다.')
  }
  return body?.data !== undefined ? body.data : body
}

function formatDateTime(value) {
  return value ? formatApiDateTimeDisplay(undefined, value) : '-'
}

function formatNumber(value) {
  if (value === null || value === undefined || value === '') return '-'
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  return n.toLocaleString('ko-KR')
}

function statusLabel(status) {
  if (!status) return '미확인'
  if (status.status === 'ERROR') return '오류'
  if (status.status === 'READY' || status.activeVersion) return '활성'
  return '대기'
}

function datasetStatusLabel(dataset, status) {
  if (!dataset?.statusKind) {
    return dataset?.collectionMode || '업무화면 조회'
  }
  if (!status) return '미확인'
  if (status.status === 'ERROR') return '오류'
  if (dataset.statusKind === 'hiraInstitution') {
    return Number(status.loadedCount || status.dbActiveCount || 0) > 0 ? '활성' : '대기'
  }
  if (dataset.statusKind === 'hiraDiseaseStats') {
    return Number(status.activeReleaseCount || 0) > 0 ? '활성' : '대기'
  }
  if (dataset.statusKind === 'openDataCache') {
    const validCount = Number(status.validEntryCount || 0)
    const entryCount = Number(status.entryCount || 0)
    if (validCount > 0) return '캐시 사용'
    if (entryCount > 0) return '만료 캐시'
    return '캐시 대기'
  }
  return statusLabel(status)
}

function statusTone(status, dataset) {
  const label = datasetStatusLabel(dataset, status)
  if (label === '활성' || label === '캐시 사용') return 'bg-emerald-50 text-emerald-700 ring-emerald-200'
  if (label === '대기' || label === '만료 캐시') return 'bg-amber-50 text-amber-700 ring-amber-200'
  if (label === '오류') return 'bg-red-50 text-red-700 ring-red-200'
  if (label === '캐시 대기') return 'bg-slate-100 text-slate-600 ring-slate-200'
  if (label.includes('실시간')) return 'bg-violet-50 text-violet-700 ring-violet-200'
  if (label.includes('DB') || label.includes('저장')) return 'bg-cyan-50 text-cyan-700 ring-cyan-200'
  return 'bg-slate-100 text-slate-600 ring-slate-200'
}

function displayVersion(dataset, status) {
  if (!status) return '-'
  if (dataset?.statusKind === 'hiraInstitution') {
    if (status.loadedActiveVersionId) return `v${status.loadedActiveVersionId}`
    return status.loadedFrom || '-'
  }
  if (dataset?.statusKind === 'hiraDiseaseStats') {
    const active = Number(status.activeReleaseCount || 0)
    const total = Number(status.releaseCount || 0)
    return total > 0 ? `활성 ${formatNumber(active)} / 전체 ${formatNumber(total)}` : '-'
  }
  if (dataset?.statusKind === 'openDataCache') {
    return status.ttlHours ? `TTL ${formatNumber(status.ttlHours)}시간` : '-'
  }
  const version = status?.activeVersion || status?.version || null
  return version?.sourceVersion || '-'
}

function displayRowCount(dataset, status) {
  if (!status) return undefined
  if (dataset?.statusKind === 'hiraInstitution') {
    return status.loadedCount ?? status.dbActiveCount
  }
  if (dataset?.statusKind === 'hiraDiseaseStats') {
    return status.metricCount
  }
  if (dataset?.statusKind === 'openDataCache') {
    return status.entryCount
  }
  const version = status?.activeVersion || status?.version || null
  return version?.rowCount ?? status?.rowCount
}

function displayImportedAt(dataset, status) {
  if (!status) return null
  if (dataset?.statusKind === 'hiraInstitution') {
    return status.loadedAt
  }
  if (dataset?.statusKind === 'hiraDiseaseStats') {
    return status.checkedAt
  }
  if (dataset?.statusKind === 'openDataCache') {
    return status.newestUpdatedAt
  }
  const version = status?.activeVersion || status?.version || null
  return version?.importedAt || status?.importedAt
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      className={`border-b-2 px-4 py-2 text-sm font-semibold ${
        active
          ? 'border-brand-navy text-brand-navy'
          : 'border-transparent text-slate-500 hover:text-slate-800'
      }`}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

function IconButton({ children, onClick, disabled, icon: Icon = RefreshCw }) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
      onClick={onClick}
      disabled={disabled}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {children}
    </button>
  )
}

function StatusPill({ status, label, dataset }) {
  const displayLabel = label || datasetStatusLabel(dataset, status)
  return (
    <span className={`inline-flex rounded px-2 py-1 text-xs font-semibold ring-1 ${statusTone(status, dataset)}`}>
      {displayLabel}
    </span>
  )
}

function insightStatusTone(status) {
  if (status === '자동화 가능') return 'bg-emerald-50 text-emerald-700 ring-emerald-200'
  if (status === '일부 구현됨' || status === '일부 자동화') return 'bg-cyan-50 text-cyan-700 ring-cyan-200'
  if (status === '별도 키 필요' || status === '반자동 권장') return 'bg-amber-50 text-amber-700 ring-amber-200'
  return 'bg-slate-100 text-slate-600 ring-slate-200'
}

function InsightDataCard({
  dataset,
  status,
  onRefresh,
  onSync,
  syncing,
  loading,
}) {
  const Icon = dataset.icon || Database
  const detailRows = INSIGHT_DETAIL_FIELDS
    .map((field) => ({ ...field, value: dataset[field.key] }))
    .filter((field) => field.value)
  const rowCount = displayRowCount(dataset, status)
  const version = displayVersion(dataset, status)
  const importedAt = displayImportedAt(dataset, status)
  const canSync = !!dataset.syncKind

  return (
    <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-slate-100 text-slate-700">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-slate-950">{dataset.title}</h2>
            <div className="mt-1 text-xs text-slate-500">{dataset.cadence} · {dataset.source}</div>
          </div>
        </div>
        {dataset.statusKind ? (
          <StatusPill status={status} dataset={dataset} />
        ) : (
          <span className={`inline-flex rounded px-2 py-1 text-xs font-semibold ring-1 ${insightStatusTone(dataset.status)}`}>
            {dataset.status}
          </span>
        )}
      </div>

      {dataset.statusKind ? (
        <dl className="mt-4 grid grid-cols-3 gap-3 text-sm">
          <div>
            <dt className="text-xs font-medium text-slate-500">활성 버전</dt>
            <dd className="mt-1 truncate font-semibold text-slate-900">{version}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-500">데이터 수</dt>
            <dd className="mt-1 font-semibold text-slate-900">{formatNumber(rowCount)}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-slate-500">최근 반영</dt>
            <dd className="mt-1 text-slate-700">{formatDateTime(importedAt)}</dd>
          </div>
        </dl>
      ) : null}

      <dl className="mt-4 grid gap-2 border-t border-slate-100 pt-4 text-xs sm:grid-cols-2">
        {detailRows.map((row) => (
          <div key={row.key} className="min-w-0">
            <dt className="font-semibold text-slate-500">{row.label}</dt>
            <dd className="mt-1 leading-5 text-slate-800">{row.value}</dd>
          </div>
        ))}
      </dl>

      {Array.isArray(dataset.sourceLinks) && dataset.sourceLinks.length > 0 ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-500">출처</span>
          {dataset.sourceLinks.map((link) => (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-300 hover:bg-white"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              {link.label}
            </a>
          ))}
        </div>
      ) : null}

      {status?.message ? (
        <div className="mt-3 rounded border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
          {status.message}
        </div>
      ) : null}

      {dataset.statusKind || canSync ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {canSync ? (
            <IconButton onClick={() => onSync(dataset)} disabled={syncing} icon={Database}>
              {syncing ? '가져오는 중...' : (dataset.syncLabel || '수동 가져오기')}
            </IconButton>
          ) : null}
          {dataset.statusKind ? (
            <IconButton onClick={() => onRefresh(dataset)} disabled={loading}>
              상태 새로고침
            </IconButton>
          ) : null}
        </div>
      ) : null}
    </article>
  )
}

function DataStatusCard({
  dataset,
  status,
  onRefresh,
  onSync,
  onUpload,
  onClearCache,
  syncing,
  uploading,
  clearing,
  loading,
}) {
  const Icon = dataset.icon || Database
  const rowCount = displayRowCount(dataset, status)
  const canSync = !!dataset.syncKind
  const canClearCache = !!dataset.cacheProvider
  const versionLabel = dataset.statusKind === 'openDataCache' ? '캐시 TTL' : '활성 버전'
  const countLabel = dataset.statusKind === 'openDataCache' ? '캐시 건수' : '데이터 수'
  const detailRows = DATA_DETAIL_FIELDS
    .map((field) => ({ ...field, value: dataset[field.key] }))
    .filter((field) => field.value)

  return (
    <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-slate-100 text-slate-700">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-bold text-slate-950">{dataset.title}</h2>
            <div className="mt-1 text-xs text-slate-500">{dataset.cadence} · {dataset.primaryPlacement}</div>
          </div>
        </div>
        <StatusPill status={status} dataset={dataset} />
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-xs font-medium text-slate-500">{versionLabel}</dt>
          <dd className="mt-1 truncate font-semibold text-slate-900">{displayVersion(dataset, status)}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium text-slate-500">{countLabel}</dt>
          <dd className="mt-1 font-semibold text-slate-900">{formatNumber(rowCount)}</dd>
        </div>
        <div className="col-span-2">
          <dt className="text-xs font-medium text-slate-500">최근 반영/확인</dt>
          <dd className="mt-1 text-slate-700">{formatDateTime(displayImportedAt(dataset, status))}</dd>
        </div>
      </dl>

      {detailRows.length > 0 ? (
        <dl className="mt-4 grid gap-2 border-t border-slate-100 pt-4 text-xs sm:grid-cols-2">
          {detailRows.map((row) => (
            <div key={row.key} className="min-w-0">
              <dt className="font-semibold text-slate-500">{row.label}</dt>
              <dd className="mt-1 leading-5 text-slate-800">{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      <div className="mt-4 text-xs leading-5 text-slate-600">{dataset.dataPlan}</div>

      {status?.message ? (
        <div className="mt-3 rounded border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
          {status.message}
        </div>
      ) : null}

      {Array.isArray(dataset.sourceLinks) && dataset.sourceLinks.length > 0 ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-500">출처</span>
          {dataset.sourceLinks.map((link) => (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-300 hover:bg-white"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              {link.label}
            </a>
          ))}
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        {canSync ? (
          <IconButton onClick={() => onSync(dataset)} disabled={syncing} icon={Database}>
            {syncing ? '가져오는 중...' : (dataset.syncLabel || '수동 가져오기')}
          </IconButton>
        ) : null}
        {dataset.uploadKind ? (
          <label className={`inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 ${uploading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
            <Database className="h-4 w-4" aria-hidden="true" />
            {uploading ? '업로드 중...' : (dataset.uploadLabel || '파일 업로드')}
            <input
              type="file"
              accept={dataset.uploadAccept || '.csv,text/csv'}
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                onUpload(dataset, file)
              }}
            />
          </label>
        ) : null}
        {dataset.statusKind ? (
          <IconButton onClick={() => onRefresh(dataset)} disabled={loading}>
            상태 새로고침
          </IconButton>
        ) : null}
        {canClearCache ? (
          <IconButton onClick={() => onClearCache(dataset)} disabled={clearing} icon={Trash2}>
            {clearing ? '비우는 중...' : '캐시 비우기'}
          </IconButton>
        ) : null}
        {!canSync && !dataset.uploadKind && !canClearCache ? (
          <span className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500">
            {dataset.collectionMode?.includes('실시간') ? '실시간 API 조회' : '업무 화면에서 조회'}
          </span>
        ) : null}
      </div>
    </article>
  )
}

function PlacementRow({ dataset }) {
  const Icon = dataset.icon || Database
  return (
    <tr className="border-t align-top">
      <td className="px-3 py-3">
        <div className="flex items-start gap-2">
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
          <div>
            <div className="font-semibold text-slate-900">{dataset.title}</div>
            <div className="mt-1 text-xs text-slate-500">{dataset.useCase}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3 text-slate-700">{dataset.primaryPlacement}</td>
      <td className="px-3 py-3 text-slate-700">{dataset.decision}</td>
      <td className="px-3 py-3 text-slate-700">{dataset.screenPlan}</td>
    </tr>
  )
}

async function fetchDatasetStatus(dataset) {
  if (!dataset?.statusKind) return null
  if (dataset.statusKind === 'fire') {
    return unwrapApiData(await api.get('/admin/open-data/fire-occurrences/status', {
      ...INLINE_ERROR_REQUEST_CONFIG,
      params: { sourceName: dataset.sourceName },
    }))
  }
  if (dataset.statusKind === 'hiraInstitution') {
    return unwrapApiData(await api.get('/admin/hira-medical-institutions/status', INLINE_ERROR_REQUEST_CONFIG))
  }
  if (dataset.statusKind === 'hiraDiseaseStats') {
    return unwrapApiData(await api.get('/operator/hira-stats/status', INLINE_ERROR_REQUEST_CONFIG))
  }
  if (dataset.statusKind === 'openDataCache') {
    return unwrapApiData(await api.get('/admin/open-data/cache/status', {
      ...INLINE_ERROR_REQUEST_CONFIG,
      params: { provider: dataset.cacheProvider },
    }))
  }
  return unwrapApiData(await api.get('/admin/open-data/fsc-insurance/status', {
    ...INLINE_ERROR_REQUEST_CONFIG,
    params: { sourceName: dataset.sourceName },
  }))
}

export default function PublicDataAdminPage() {
  const [tab, setTab] = React.useState('status')
  const [statuses, setStatuses] = React.useState({})
  const [loading, setLoading] = React.useState(false)
  const [syncingId, setSyncingId] = React.useState('')
  const [uploadingId, setUploadingId] = React.useState('')
  const [clearingId, setClearingId] = React.useState('')
  const [message, setMessage] = React.useState('')
  const [messageTone, setMessageTone] = React.useState('slate')

  const loadStatus = React.useCallback(async (dataset) => {
    if (!dataset?.statusKind) return
    setLoading(true)
    setMessage('')
    try {
      const data = await fetchDatasetStatus(dataset)
      setStatuses((prev) => ({ ...prev, [dataset.id]: data || {} }))
    } catch (e) {
      setMessage(getApiErrorMessage(e, `${dataset.title} 상태를 불러오지 못했습니다.`))
      setMessageTone('error')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadAllStatus = React.useCallback(async () => {
    const targets = [...DATASETS, ...INSIGHT_DATASETS].filter((d) => d.statusKind)
    setLoading(true)
    setMessage('')
    try {
      const entries = await Promise.all(targets.map(async (dataset) => {
        try {
          const data = await fetchDatasetStatus(dataset)
          return [dataset.id, data || {}]
        } catch (e) {
          return [dataset.id, { status: 'ERROR', message: getApiErrorMessage(e, '상태 조회 실패') }]
        }
      }))
      setStatuses((prev) => ({ ...prev, ...Object.fromEntries(entries) }))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    loadAllStatus()
  }, [loadAllStatus])

  const syncDataset = async (dataset) => {
    if (!dataset?.syncKind) return
    const kind = dataset.syncKind
    setSyncingId(dataset.id)
    setMessage('')
    setMessageTone('slate')
    try {
      if (kind === 'fire') {
        await api.post('/admin/open-data/fire-occurrences/sync', null, INLINE_ERROR_REQUEST_CONFIG)
        await loadStatus(dataset)
        setMessage(`${dataset.title} 가져오기를 완료했습니다.`)
      } else if (kind === 'hiraInstitutionReload') {
        await api.post('/admin/hira-medical-institutions/reload', null, INLINE_ERROR_REQUEST_CONFIG)
        await loadStatus(dataset)
        setMessage(`${dataset.title} 캐시를 다시 읽었습니다.`)
      } else if (kind === 'general') {
        await api.post('/admin/open-data/fsc-insurance/general-subscriptions/sync', null, INLINE_ERROR_REQUEST_CONFIG)
        await loadStatus(dataset)
        setMessage(`${dataset.title} 가져오기를 완료했습니다.`)
      } else if (kind.startsWith('market:')) {
        await api.post('/admin/open-data/fsc-insurance/market-stats/sync', null, {
          ...INLINE_ERROR_REQUEST_CONFIG,
          params: { datasetType: kind.slice('market:'.length) },
        })
        await loadStatus(dataset)
        setMessage(`${dataset.title} 가져오기를 완료했습니다.`)
      } else {
        await api.post('/admin/open-data/fsc-insurance/company-metrics/sync', null, {
          ...INLINE_ERROR_REQUEST_CONFIG,
          params: { sector: kind },
        })
        await loadStatus(dataset)
        setMessage(`${dataset.title} 가져오기를 완료했습니다.`)
      }
      setMessageTone('success')
    } catch (e) {
      setMessage(getApiErrorMessage(e, `${dataset.title} 가져오기에 실패했습니다.`))
      setMessageTone('error')
    } finally {
      setSyncingId('')
    }
  }

  const uploadDataset = async (dataset, file) => {
    if (!dataset?.uploadKind || !file) return
    setUploadingId(dataset.id)
    setMessage('')
    setMessageTone('slate')
    try {
      if (dataset.uploadKind === 'fireCsv') {
        const body = new FormData()
        body.append('file', file)
        body.append('sourceVersion', file.name)
        body.append('activate', 'true')
        body.append('async', 'false')
        await api.post('/admin/open-data/fire-occurrences/imports', body, {
          ...INLINE_ERROR_REQUEST_CONFIG,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        await loadStatus(dataset)
        setMessage(`${dataset.title} CSV 업로드를 완료했습니다.`)
        setMessageTone('success')
      } else if (dataset.uploadKind === 'hiraInstitutionFile') {
        const body = new FormData()
        body.append('file', file)
        body.append('label', file.name)
        body.append('async', 'false')
        await api.post('/admin/hira-medical-institutions/versions', body, {
          ...INLINE_ERROR_REQUEST_CONFIG,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        await loadStatus(dataset)
        setMessage(`${dataset.title} 기관 파일 업로드를 완료했습니다.`)
        setMessageTone('success')
      } else if (dataset.uploadKind === 'hiraDiseaseStatsXlsx') {
        const body = new FormData()
        body.append('file', file)
        body.append('releaseName', file.name)
        body.append('activate', 'true')
        body.append('replace', 'false')
        body.append('async', 'false')
        await api.post('/operator/hira-stats/import-disease-xlsx', body, {
          ...INLINE_ERROR_REQUEST_CONFIG,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        await loadStatus(dataset)
        setMessage(`${dataset.title} 통계 파일 업로드를 완료했습니다.`)
        setMessageTone('success')
      }
    } catch (e) {
      setMessage(getApiErrorMessage(e, `${dataset.title} 파일 업로드에 실패했습니다.`))
      setMessageTone('error')
    } finally {
      setUploadingId('')
    }
  }

  const clearDatasetCache = async (dataset) => {
    if (!dataset?.cacheProvider) return
    setClearingId(dataset.id)
    setMessage('')
    setMessageTone('slate')
    try {
      const result = unwrapApiData(await api.post('/admin/open-data/cache/clear', null, {
        ...INLINE_ERROR_REQUEST_CONFIG,
        params: { provider: dataset.cacheProvider },
      }))
      await loadStatus(dataset)
      setMessage(`${dataset.title} 캐시 ${formatNumber(result?.deletedCount || 0)}건을 비웠습니다.`)
      setMessageTone('success')
    } catch (e) {
      setMessage(getApiErrorMessage(e, `${dataset.title} 캐시 비우기에 실패했습니다.`))
      setMessageTone('error')
    } finally {
      setClearingId('')
    }
  }

  const messageClass = messageTone === 'error'
    ? 'border-red-200 bg-red-50 text-red-700'
    : messageTone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : 'border-slate-200 bg-slate-50 text-slate-700'

  return (
    <div className="mx-auto max-w-7xl px-4 py-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 border-l-4 border-brand-navy pl-3">
            <h1 className="text-xl font-bold text-slate-900">공공데이터관리</h1>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {SOURCE_LINKS.map((link) => (
            <a
              key={link.url}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              href={link.url}
              target={link.internal ? undefined : '_blank'}
              rel={link.internal ? undefined : 'noreferrer'}
            >
              {link.label}
            </a>
          ))}
          <IconButton onClick={loadAllStatus} disabled={loading}>
            전체 새로고침
          </IconButton>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-1 border-b border-slate-200">
        <TabButton active={tab === 'status'} onClick={() => setTab('status')}>동기화 현황</TabButton>
        <TabButton active={tab === 'insight'} onClick={() => setTab('insight')}>인사이트 데이터</TabButton>
        <TabButton active={tab === 'placement'} onClick={() => setTab('placement')}>화면 적용안</TabButton>
      </div>

      {message ? (
        <div className={`mt-4 rounded-lg border px-3 py-2 text-sm ${messageClass}`}>{message}</div>
      ) : null}

      {tab === 'status' ? (
        <div className="mt-6 space-y-4">
          {DATASETS.map((dataset) => (
            <DataStatusCard
              key={dataset.id}
              dataset={dataset}
              status={statuses[dataset.id]}
              onRefresh={loadStatus}
              onSync={syncDataset}
              onUpload={uploadDataset}
              onClearCache={clearDatasetCache}
              syncing={syncingId === dataset.id}
              uploading={uploadingId === dataset.id}
              clearing={clearingId === dataset.id}
              loading={loading}
            />
          ))}
        </div>
      ) : null}

      {tab === 'insight' ? (
        <div className="mt-6 space-y-4">
          {INSIGHT_DATASETS.map((dataset) => (
            <InsightDataCard
              key={dataset.id}
              dataset={dataset}
              status={statuses[dataset.id]}
              onRefresh={loadStatus}
              onSync={syncDataset}
              syncing={syncingId === dataset.id}
              loading={loading}
            />
          ))}
        </div>
      ) : null}

      {tab === 'placement' ? (
        <div className="mt-6 space-y-4">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-[980px] w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-500">
                  <tr>
                    <th className="px-3 py-2">데이터</th>
                    <th className="px-3 py-2">추천 위치</th>
                    <th className="px-3 py-2">방향</th>
                    <th className="px-3 py-2">화면 구성</th>
                  </tr>
                </thead>
                <tbody>
                  {DATASETS.map((dataset) => (
                    <PlacementRow key={dataset.id} dataset={dataset} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-bold text-slate-900">보험사 일반현황 데이터</h2>
            <div className="mt-3 grid gap-3 md:grid-cols-4">
              {INSURANCE_COMPANY_FIELDS.map((item) => (
                <div key={item.label} className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-xs font-semibold text-slate-500">{item.label}</div>
                  <div className="mt-1 text-sm text-slate-800">{item.text}</div>
                </div>
              ))}
            </div>
          </section>
          </div>
      ) : null}
    </div>
  )
}
